import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { getStats, createStat, updateStat, deleteStat } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Loader2, GripVertical } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

export default function AdminStats() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStat, setEditingStat] = useState(null);
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    order: 0,
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const res = await getStats();
      setStats(res.data);
    } catch (err) {
      toast.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingStat) {
        await updateStat(editingStat.id, formData);
        toast.success('Stat updated');
      } else {
        await createStat({ ...formData, order: stats.length });
        toast.success('Stat added');
      }
      loadStats();
      setDialogOpen(false);
      resetForm();
    } catch (err) {
      toast.error('Failed to save stat');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (stat) => {
    setEditingStat(stat);
    setFormData({
      value: stat.value,
      label: stat.label,
      order: stat.order || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this stat?')) return;
    
    try {
      await deleteStat(id);
      toast.success('Stat deleted');
      loadStats();
    } catch (err) {
      toast.error('Failed to delete stat');
    }
  };

  const resetForm = () => {
    setEditingStat(null);
    setFormData({ value: '', label: '', order: 0 });
  };

  return (
    <AdminLayout title="Stats Management">
      <div className="space-y-6" data-testid="admin-stats">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">Homepage Stats</h1>
            <p className="text-[#888] text-sm">Manage the statistics displayed on homepage</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <button
                data-testid="add-stat-btn"
                className="bg-[#ff5a1f] text-white px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-[#e04e1a] transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Stat
              </button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-syne text-xl">
                  {editingStat ? 'Edit Stat' : 'Add Stat'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="stat-form">
                <div>
                  <label className="block text-[#888] text-sm mb-2">Value *</label>
                  <input
                    type="text"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="e.g., 150+, 500%, 10M+"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Label *</label>
                  <input
                    type="text"
                    value={formData.label}
                    onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                    required
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                    placeholder="e.g., Clients Served, Avg ROI"
                  />
                </div>
                
                <div>
                  <label className="block text-[#888] text-sm mb-2">Display Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full bg-[#121212] border border-[#333] focus:border-[#ff5a1f] text-white px-4 py-3 text-sm outline-none"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    className="flex-1 border border-[#333] text-white px-4 py-3 text-sm font-semibold hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#ff5a1f] text-white px-4 py-3 text-sm font-semibold hover:bg-[#e04e1a] flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingStat ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#ff5a1f] animate-spin" />
          </div>
        ) : stats.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat) => (
              <div
                key={stat.id}
                className="bg-[#0A0A0A] border border-[#1F1F1F] p-6 group"
                data-testid={`stat-card-${stat.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <GripVertical className="w-4 h-4 text-[#333] cursor-move" />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(stat)}
                      className="text-[#888] hover:text-[#ff5a1f] p-1"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(stat.id)}
                      className="text-[#888] hover:text-red-500 p-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="font-syne text-4xl font-bold text-[#ff5a1f] mb-2">
                  {stat.value}
                </p>
                <p className="text-[#888] text-sm uppercase tracking-wider">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-16 text-center">
            <p className="text-[#888] mb-4">No stats yet. Add your first stat to display on homepage.</p>
            <button
              onClick={() => setDialogOpen(true)}
              className="text-[#ff5a1f] font-semibold hover:underline"
            >
              Add your first stat
            </button>
          </div>
        )}

        {/* Preview */}
        {stats.length > 0 && (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
            <h3 className="font-syne font-bold text-white mb-4">Preview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 py-8 border-y border-[#1F1F1F]">
              {stats.map((stat) => (
                <div key={stat.id} className="text-center">
                  <p className="font-syne text-3xl font-bold text-[#ff5a1f] mb-2">
                    {stat.value}
                  </p>
                  <p className="text-[#888] text-xs uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
